/* Feb-2026 · Route Safety Valve admin card.
 *
 * Pinned inside the Admin → Keys page so ops can see at a glance
 * whether the native-Anthropic route has tripped and, if so, hit a
 * manual reset without shelling into the DB.
 *
 * Backend surface:
 *   GET  /api/admin/route-valve/status
 *   POST /api/admin/route-valve/reset
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, ShieldCheck, RotateCcw, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { routeValveApi } from '@/lib/api';

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const min = Math.round((Date.now() - d.getTime()) / 60000);
    if (min < 1)  return 'just now';
    if (min < 60) return `${min} min ago`;
    const hrs = Math.round(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch { return '—'; }
}

export default function RouteValveCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await routeValveApi.status();
      setState(r);
    } catch (e) {
      toast.error('Failed to load safety-valve status.');
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onReset = async () => {
    if (!window.confirm('Manually reset the safety valve? '
                          + 'Native-Anthropic requests will start firing again.')) return;
    setResetting(true);
    try {
      const r = await routeValveApi.reset();
      setState(r);
      toast.success('Safety valve reset — native route is live again.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed.');
    } finally { setResetting(false); }
  };

  if (!state) {
    return (
      <div className="mt-4 rounded-md border border-border bg-card p-4 text-[12px] text-muted-foreground"
           data-testid="route-valve-card-loading">
        Loading safety-valve status…
      </div>
    );
  }

  const tripped = !!state.tripped;
  const consecutive = state.consecutive ?? 0;

  return (
    <div
      className={`mt-4 rounded-md border p-4 space-y-3 ${
        tripped
          ? 'border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40'
          : 'border-border bg-card'
      }`}
      data-testid="route-valve-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
            {tripped
              ? <ShieldAlert className="h-4 w-4 text-rose-700" />
              : <ShieldCheck className="h-4 w-4 text-emerald-700" />}
            Native-Anthropic Route Safety Valve
          </div>
          <div className="text-[11px] text-muted-foreground">
            Auto-flips drafting to the Emergent Universal key after
            {' '}{state.trip_threshold ?? 3} consecutive Anthropic errors within
            {' '}{Math.round((state.window_seconds ?? 600) / 60)} min. Auto-resets after
            {' '}{Math.round((state.cooldown_seconds ?? 1800) / 60)} min of clean calls.
          </div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={load} disabled={busy}
                  data-testid="route-valve-refresh-btn"
                  className="h-8 gap-1 text-[12px]">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {tripped && (
            <Button size="sm" onClick={onReset} disabled={resetting}
                    className="h-8 gap-1 bg-rose-600 text-[12px] hover:bg-rose-700"
                    data-testid="route-valve-reset-btn">
              {resetting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RotateCcw className="h-3.5 w-3.5" />}
              Reset now
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11.5px] sm:grid-cols-4">
        <Stat label="Status"
                value={tripped
                  ? <span className="font-semibold text-rose-700">Tripped</span>
                  : <span className="font-semibold text-emerald-700">Clear</span>}
                testid="route-valve-status" />
        <Stat label="Consecutive errors"
                value={consecutive}
                testid="route-valve-consecutive" />
        <Stat label="Tripped at"
                value={fmtWhen(state.tripped_at)}
                testid="route-valve-tripped-at" />
        <Stat label="Last error"
                value={fmtWhen(state.last_error_at)}
                testid="route-valve-last-error-at" />
      </div>

      {state.last_error_body && (
        <details className="rounded border border-border bg-background/60">
          <summary className="cursor-pointer px-2 py-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            Last error body
          </summary>
          <pre className="max-h-40 overflow-auto p-2 font-mono text-[10.5px] leading-snug"
               data-testid="route-valve-last-error-body">
{state.last_error_body}
          </pre>
        </details>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded border border-border bg-background/60 px-2 py-1.5"
         data-testid={testid}>
      <div className="text-[9.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[13px]">{value}</div>
    </div>
  );
}
